import { useMemo, useState } from 'react'
import { useAdminDeviceMutes, useSetDeviceMute, useUnmuteAll } from '../../../hooks/admin/useCatchAllAlerts'

export default function DeviceMutesPage() {
  const { data: rows, isLoading } = useAdminDeviceMutes()
  const setMute = useSetDeviceMute()
  const unmuteAll = useUnmuteAll()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const all = rows ?? []
    if (!q) return all
    return all.filter(
      (r) =>
        String(r.serialNumber).includes(q) ||
        r.sigfoxId.toLowerCase().includes(q) ||
        (r.locationName ?? '').toLowerCase().includes(q)
    )
  }, [rows, search])

  const mutedCount = (rows ?? []).filter((r) => r.muted).length

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Device Mutes</h1>
          <p className="text-gray-600 mt-1">
            Muted devices are skipped by the catch-all monitor. {mutedCount} device{mutedCount === 1 ? '' : 's'} currently muted.
          </p>
        </div>
        <button
          type="button"
          onClick={() => unmuteAll.mutate()}
          disabled={unmuteAll.isPending || mutedCount === 0}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg disabled:opacity-50"
        >
          {unmuteAll.isPending ? 'Unmuting...' : 'Unmute all'}
        </button>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by serial, Sigfox ID, or location"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
      />

      {isLoading ? (
        <p className="text-gray-600">Loading devices...</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Muted</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Serial</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Sigfox ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((row) => (
                <tr key={row.deviceId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={row.muted}
                      onChange={(e) => setMute.mutate({ deviceId: row.deviceId, muted: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium">{row.serialNumber}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-sm">{row.sigfoxId}</td>
                  <td className="px-4 py-3">{row.locationName ?? '-'}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                    No devices match this search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
